import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiEnvelope } from './api-contracts';

const isApiEnvelope = (value: unknown): value is ApiEnvelope<unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate['status'] === 'string'
    && 'data' in candidate
    && 'error' in candidate
    && 'message' in candidate
    && 'meta' in candidate;
};

const isSilentRequest = (request: HttpRequest<unknown>): boolean => {
  const silentFromParams = request.params.get('silent');
  if (silentFromParams?.toLowerCase() === 'true') {
    return true;
  }

  const [, query = ''] = request.urlWithParams.split('?');
  if (!query) {
    return false;
  }

  return new URLSearchParams(query).get('silent')?.toLowerCase() === 'true';
};

const normalizeServerMessage = (message: unknown): string => {
  if (typeof message === 'string') {
    return message.trim();
  }

  return '';
};

const notifySuccess = (messages: MessageService, envelope: ApiEnvelope<unknown>): void => {
  const message = normalizeServerMessage(envelope.message);
  if (!message) {
    return;
  }

  messages.add({
    severity: 'success',
    summary: 'Success',
    detail: message
  });
};

const notifyError = (messages: MessageService, error: HttpErrorResponse): void => {
  const message = normalizeServerMessage((error.error as { Message?: string } | null)?.Message)
    || normalizeServerMessage(error.message);
  if (!message) {
    return;
  }

  const severity = error.status >= 500 ? 'error' : 'warn';
  const summary = severity === 'error' ? 'Error' : 'Warning';
  messages.add({
    severity,
    summary,
    detail: message
  });
};

const unwrapEvent = (event: HttpEvent<unknown>): HttpEvent<unknown> => {
  if (!(event instanceof HttpResponse)) {
    return event;
  }

  if (!isApiEnvelope(event.body)) {
    return event;
  }

  return event.clone({ body: event.body.data });
};

const normalizeError = (error: unknown): unknown => {
  if (!(error instanceof HttpErrorResponse) || !isApiEnvelope(error.error)) {
    return error;
  }

  const envelope = error.error;
  const normalizedErrorPayload = {
    ...envelope.error?.details,
    Error: envelope.error?.code ?? `http_${error.status}`,
    ReasonCode: envelope.error?.reasonCode,
    Message: envelope.message ?? envelope.error?.message,
    Meta: envelope.meta
  };

  return new HttpErrorResponse({
    error: normalizedErrorPayload,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined
  });
};

export const apiEnvelopeInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const messages = inject(MessageService);
  const silent = isSilentRequest(request);

  return next(request).pipe(
    map(event => {
      if (!silent && event instanceof HttpResponse && isApiEnvelope(event.body) && event.body.status === 'success') {
        notifySuccess(messages, event.body);
      }

      return unwrapEvent(event);
    }),
    catchError(error => {
      const normalized = normalizeError(error);
      if (!silent && normalized instanceof HttpErrorResponse) {
        notifyError(messages, normalized);
      }

      return throwError(() => normalized);
    })
  );
};
